// Content for "Astrology & Bhakti" series, Part 3
// Class by HG Balarāma Śakti Dāsa, Bhakti Saṅgha, on SB 5.14.8 (30 October 2021)
//
// This part reproduces Section 4 of the class, "Duties Expected of a Qualified
// Practitioner," and "Theme VI" as they appear on the presentation slides. No
// audio-only prose commentary is included; the closing diagram is a visual summary
// of the slide content itself.

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Śrīmad-Bhāgavatam 11.2.6',
        sloka: `bhajanti ye yathā devān
devā api tathaiva tān
chāyeva karma-sacivāḥ
sādhavo dīna-vatsalāḥ`,
        translation:
            'Those who worship the devatās (<strong>bhajanti ye yathā devān</strong>) receive reciprocation from the devatās in a way corresponding to the offering (<strong>devā api tathaiva tān</strong>). The devatās are attendants of karma (<strong>chāyeva karma-sacivāḥ</strong>), like a person\'s shadow (<strong>chāyā iva</strong>), but devotees are affectionate to the fallen (<strong>sādhavo dīna-vatsalāḥ</strong>).',
    },
    blocks: [
        {
            type: 'heading',
            headingText: 'Section 4 — What Then Is the Role of Astrology in a Devotee\'s Life?',
        },
        {
            type: 'text',
            content: '<strong>(Śrīla Prabhupāda commentary - 11.2.6)</strong>',
        },
        {
            type: 'text',
            content:
                'The words <em>chāyeva karma-sacivāḥ</em> are significant here. <em>Chāyā</em> means "shadow." The shadow of the body precisely follows the movements of the body. The shadow has no power to move in a way different from the movement of the body. Similarly, as stated here — <em>bhajanti ye yathā devān devā api tathaiva tān</em> — the results the demigods award to living beings correspond exactly to the living beings\' actions.',
        },
        {
            type: 'text',
            content:
                'The demigods are empowered by the Lord to follow precisely the particular karma of a living entity in awarding him happiness and distress. Just as a shadow cannot move independently, the demigods cannot punish or reward a living being independently. Although the demigods are millions of times more powerful than the human beings on earth, they are ultimately tiny servants of God, whom the Lord allows to play as the controllers of the universe.',
        },
        {
            type: 'text',
            content:
                'In the Fourth Canto of Śrīmad-Bhāgavatam, Pṛthu Mahārāja, an empowered incarnation of the Lord, states that even the demigods are subject to punishment by the Lord if they deviate from His laws. On the other hand, devotees of the Lord such as Nārada Muni, by their potent preaching, can interfere in the karma of a living being by persuading him to give up his fruitive activity and mental speculation and surrender to the Supreme Personality of Godhead.',
        },
        {
            type: 'text',
            content:
                'In material existence, one works hard under the bondage of ignorance. But if one becomes enlightened by association with a pure devotee of the Lord, one can understand one\'s actual position as an eternal servant of God. By rendering such service, one dissolves his attachment to the material world and the reactions of his previous activities, and as a surrendered soul he is endowed with unlimited spiritual freedom in the service of the Lord.',
        },
        {
            type: 'text',
            content: 'In this regard, the Brahma-saṁhitā (5.54) states:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Brahma-saṁhitā 5.54',
                sloka: `yas tv indragopam athavendram aho sva-karma-
bandhānurūpa-phala-bhājanam ātanoti
karmāṇi nirdahati kintu ca bhakti-bhājāṁ
govindam ādi-puruṣaṁ tam ahaṁ bhajāmi`,
                translation:
                    '"I adore the primeval Lord, Govinda, who burns to the root all the fruitive activities of those imbued with devotion. For those who walk the path of work — no less for Indra, king of the demigods, than for the tiny insect indragopa — He impartially ordains the due enjoyments of the fruits of activities in accordance with the chain of works previously performed."',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Even the demigods are bound to the laws of karma, whereas a pure devotee of the Lord, having completely given up the desire for material enjoyment, successfully burns to ashes all traces of karma.</strong>',
        },
        {
            type: 'heading',
            headingText: 'Duties Expected of a Qualified Practitioner',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhakti-sandarbha',
                sloka: `ananuṣṭhānato doṣo
bhakty-aṅgānāṁ prajāyate
na karmaṇām akaraṇād
eṣa bhakty-adhikāriṇām

niṣiddhācārato daivāt
prāyaścittaṁ tu nocitam
iti vaiṣṇava-śāstrāṇāṁ
rahasyaṁ tad-vidāṁ matam`,
                translation:
                    'The person qualified for bhakti (<strong>eṣa bhakty-adhikāriṇām</strong>) is at fault (<strong>doṣaḥ prajāyate</strong>) for failing to perform all of the important aṅgas of bhakti (<strong>bhakty-aṅgānāṁ akaraṇād</strong>). But he is not at fault for failing to perform the duties of varṇa and āśrama (<strong>na karmaṇām akaraṇād</strong>). If he, by chance (<strong>daivāt</strong>), happens to commit some sin (<strong>niṣiddhācārato</strong>), there is no atonement prescribed for him (<strong>prāyaścittaṁ tu na ucitam</strong>). This is the opinion (<strong>iti matam</strong>) of those who know the secret of Vaiṣṇava scriptures (<strong>vaiṣṇava-śāstrāṇāṁ rahasyaṁ tad-vidāṁ</strong>).',
            },
        },
        {
            type: 'heading',
            headingText: 'Won\'t the Devatās Become Upset If I Just Worship You?',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.25.42',
                sloka: `mad-bhayād vāti vāto 'yaṁ
sūryas tapati mad-bhayāt
varṣatīndro dahaty agnir
mṛtyuś carati mad-bhayāt`,
                translation:
                    'Out of fear of me (<strong>mad-bhayāt</strong>), the wind blows (<strong>vāto \'yaṁ vāti</strong>). Out of fear of me, the sun shines (<strong>sūryas tapati mad-bhayāt</strong>). Out of fear of me, Indra pours rain (<strong>varṣaty indro</strong>) and fire burns (<strong>dahaty agnir</strong>). Out of fear of me, death flees (<strong>mṛtyuś carati mad-bhayāt</strong>).',
            },
        },
        {
            type: 'text',
            content: '<strong>Never — all of them are fearful of Me, their master.</strong>',
        },
        {
            type: 'dialogue',
            objection:
                'You have just said that devotees worship you without worshipping others (<em>ananyayā bhaktyā</em>). Will the devatās, not being worshipped, become angry, and give suffering to your devotees sometimes?',
            response:
                'The Lord answers with pride. The śruti also says: <em>bhīṣāsmād vātaḥ pavate bhīṣodeti sūryyaḥ, bhīṣāsmād agniś cendraś ca mṛtyur dhāvati pañcama</em> — the wind blows out of fear of the Lord, the sun rises in fear, and Agni and Indra move, and death flees out of fear of the Lord. <strong>If they sometimes harass my devotees, I do not delay in removing them from their positions.</strong>',
        },
        {
            type: 'diagram',
            diagram: {
                mermaidCode: `flowchart TD
    K["A person's actions (karma)"] --> D{"Has genuine\\ndevotional surrender\\nto Kṛṣṇa arisen?"}
    D -->|"No"| S["Devatās, as karma's 'shadow',\\naward happiness/distress\\nexactly matching that karma"]
    S --> ST["Astrology can read this —\\nit is reading karma"]
    D -->|"Yes"| M["Karma is burned to ashes\\n(Brahma-saṁhitā 5.54)"]
    M --> KR["Kṛṣṇa personally orchestrates\\nhappiness and distress\\n(for spiritual advancement)"]
    KR --> NA["Devatās have no jurisdiction —\\nastrology has nothing left to read"]`,
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'text',
            content:
                'The next part in this series continues with Section 5: The basis of the astrological chart and its calculations.',
        },
    ],
};

export default content;
